import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'

import {
  mdiCheck,
  mdiContentCopy,
  mdiFormatBold,
  mdiFormatItalic,
  mdiFormatListBulleted,
  mdiFormatListChecks,
  mdiFormatListNumbered,
  mdiFormatUnderline,
  mdiLink,
  mdiLinkOff,
  mdiMicrophoneOutline,
  mdiPaperclip,
  mdiPencil,
  mdiRedo,
  mdiUndo,
} from '@mdi/js'
import Icon from '@mdi/react'
import {
  BoldItalicUnderlineToggles,
  ButtonWithTooltip,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  type MDXEditorMethods,
  MultipleChoiceToggleGroup,
  quotePlugin,
  Separator,
  thematicBreakPlugin,
  toolbarPlugin,
} from '@mdxeditor/editor'

import { battleshipGrey } from '@constants/colors'
import { useAddLinkModal } from '@contexts/addLinkModalProvider'
import {
  IMarkDownRichTextEditor,
  MarkDownRichTextEditorRef,
} from '@interfaces/components/molecules/MarkDownRichTextEditor'

import MarkDownConverter from '../MarkDownConverter'

import './styles.scss'
import '@mdxeditor/editor/style.css'

const MarkDownRichTextEditor: ForwardRefRenderFunction<
  MarkDownRichTextEditorRef,
  IMarkDownRichTextEditor
> = ({ onSelectFile, onClickTextToSpeech, isListening = false, withSelectFile, ...props }, ref) => {
  const inputRef = useRef<MDXEditorMethods>(null)
  const inputFile = useRef<HTMLInputElement>(null)

  const addLinkModal = useAddLinkModal()

  useEffect(() => {
    if (typeof props.markdown === 'string') {
      inputRef.current?.setMarkdown(props.markdown)
    }
  }, [props.markdown])

  useImperativeHandle(ref, () => {
    if (inputRef.current) {
      return inputRef.current
    } else {
      return undefined
    }
  })

  const updateContentWithLink = () => {
    const selection = window.getSelection()
    const selectedText = selection?.toString()
    if (inputRef.current) {
      addLinkModal.show({
        content: selectedText || '',
        onClose: () => {},
        onSave: inputRef.current.insertMarkdown,
      })
    }
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.files?.length) {
      const file = event.target.files[0]
      if (file.size > 25000000) {
        alert('The file size should be less than 25MB.')
        return
      }
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onloadend = () => {
        if (onSelectFile) {
          onSelectFile({ file, base64: reader.result })
        }
      }
    }
  }

  if (props.readOnly) {
    return <MarkDownConverter value={props.markdown || ''} />
  }

  return (
    <MDXEditor
      ref={inputRef}
      plugins={[
        toolbarPlugin({
          toolbarContents: () => (
            <>
              <BoldItalicUnderlineToggles />
              <Separator />
              <ListsToggle />
              <Separator />
              <ButtonWithTooltip title="Create link" onClick={updateContentWithLink}>
                <Icon path={mdiLink} size={1} color={battleshipGrey} />
              </ButtonWithTooltip>
              {withSelectFile ? (
                <ButtonWithTooltip
                  title="Upload file"
                  onClick={() => {
                    inputFile.current?.click()
                  }}
                >
                  <Icon path={mdiPaperclip} size={1} color={battleshipGrey} />
                  <input
                    ref={inputFile}
                    id="replyMessageFileUpload"
                    type="file"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </ButtonWithTooltip>
              ) : null}
              {onClickTextToSpeech ? (
                <>
                  <Separator />
                  <MultipleChoiceToggleGroup
                    items={[
                      {
                        active: isListening,
                        title: isListening
                          ? 'Deactivate text to speech'
                          : 'Activate text to speech',
                        onChange: (active) => {
                          onClickTextToSpeech(active)
                          if (active) inputRef.current?.focus()
                        },
                        contents: (
                          <Icon path={mdiMicrophoneOutline} color={battleshipGrey} size={1} />
                        ),
                      },
                    ]}
                  />
                </>
              ) : null}
            </>
          ),
        }),
        headingsPlugin(),
        listsPlugin(),
        quotePlugin(),
        thematicBreakPlugin(),
        markdownShortcutPlugin(),
        linkDialogPlugin(),
        linkPlugin(),
      ]}
      iconComponentFor={(name) => {
        if (name === 'undo') {
          return <Icon path={mdiUndo} size={1} color={battleshipGrey} />
        }
        if (name === 'redo') {
          return <Icon path={mdiRedo} size={1} color={battleshipGrey} />
        }
        if (name === 'format_bold') {
          return <Icon path={mdiFormatBold} size={1} color={battleshipGrey} />
        }
        if (name === 'format_italic') {
          return <Icon path={mdiFormatItalic} size={1} color={battleshipGrey} />
        }
        if (name === 'format_underlined') {
          return <Icon path={mdiFormatUnderline} size={1} color={battleshipGrey} />
        }
        if (name === 'link') {
          return <Icon path={mdiLink} size={1} color={battleshipGrey} />
        }
        if (name === 'link_off') {
          return <Icon path={mdiLinkOff} size={1} color={battleshipGrey} />
        }
        if (name === 'content_copy') {
          return <Icon path={mdiContentCopy} size={1} color={battleshipGrey} />
        }
        if (name === 'check') {
          return <Icon path={mdiCheck} size={1} color={battleshipGrey} />
        }
        if (name === 'edit') {
          return <Icon path={mdiPencil} size={1} color={battleshipGrey} />
        }
        if (name === 'format_list_bulleted') {
          return <Icon path={mdiFormatListBulleted} size={1} color={battleshipGrey} />
        }
        if (name === 'format_list_numbered') {
          return <Icon path={mdiFormatListNumbered} size={1} color={battleshipGrey} />
        }
        if (name === 'format_list_checked') {
          return <Icon path={mdiFormatListChecks} size={1} color={battleshipGrey} />
        }
        return <div />
      }}
      toMarkdownOptions={{ resourceLink: true }}
      {...props}
      // must be last to not override
      contentEditableClassName={`MDRTTextInput ${props.contentEditableClassName || ''}`}
    />
  )
}

export default forwardRef(MarkDownRichTextEditor)
